import React, { useMemo, useRef, useState } from 'react';
import { Form, Input, Select, Tabs, Divider } from 'antd';
import type { NamePath } from 'antd/es/form/interface';
import { Field } from './Field';
import { isDefaultLocale, useActiveLocales } from '../hooks/useActiveLocales';

export type LocalizedFieldConfig = {
  /** Top-level form field name for the default (EN) locale. */
  key: string;
  /** Key inside translations[locale]. Defaults to `key`. */
  translationKey?: string;
  label: string;
  placeholder?: string;
  textarea?: boolean;
  /** A list of short strings, entered one per tag. */
  tags?: boolean;
  required?: boolean;
  rows?: number;
};

type TranslationMap = Record<string, Record<string, unknown>>;

function fieldTranslationKey(f: LocalizedFieldConfig): string {
  return f.translationKey ?? f.key;
}

function normalizeStringList(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.map(String).map((s) => s.trim()).filter(Boolean);
  }
  if (typeof value === 'string' && value.trim()) {
    return value.split('\n').map((s) => s.trim()).filter(Boolean);
  }
  return [];
}

/**
 * Preserve locales that were not present in the form submit payload
 * (e.g. inactive / historical locales without Form.Items).
 */
function mergePreservedLocales(
  cleaned: TranslationMap,
  formTranslations: TranslationMap | undefined,
  existing?: TranslationMap | null,
): TranslationMap {
  const submittedLocales = new Set(Object.keys(formTranslations || {}));
  const merged: TranslationMap = { ...cleaned };

  if (existing) {
    for (const [locale, data] of Object.entries(existing)) {
      if (isDefaultLocale(locale)) continue;
      if (submittedLocales.has(locale)) continue;
      if (data && typeof data === 'object') {
        merged[locale] = data;
      }
    }
  }

  return merged;
}

/**
 * Strip empty locales from a translations map and merge with existing
 * translations so unregistered locales are not wiped on save.
 *
 * Returns `null` when empty so PATCH clears translations intentionally.
 */
export function normalizeTranslations(
  translations: TranslationMap | undefined,
  fields: LocalizedFieldConfig[],
  existing?: TranslationMap | null,
): TranslationMap | null {
  const cleaned: TranslationMap = {};

  if (translations && typeof translations === 'object') {
    for (const [locale, data] of Object.entries(translations)) {
      if (!data || typeof data !== 'object') continue;
      if (isDefaultLocale(locale)) continue;

      const entry: Record<string, unknown> = {};
      let hasValue = false;

      for (const f of fields) {
        const tKey = fieldTranslationKey(f);
        const raw = data[tKey];
        if (typeof raw === 'string') {
          const trimmed = raw.trim();
          if (trimmed) {
            entry[tKey] = trimmed;
            hasValue = true;
          }
        } else if (Array.isArray(raw)) {
          const list = normalizeStringList(raw);
          if (list.length > 0) {
            entry[tKey] = list;
            hasValue = true;
          }
        }
      }

      if (hasValue) cleaned[locale] = entry;
    }
  }

  const merged = mergePreservedLocales(cleaned, translations, existing);
  return Object.keys(merged).length > 0 ? merged : null;
}

function renderInput(f: LocalizedFieldConfig) {
  if (f.tags) {
    return (
      <Select
        mode="tags"
        placeholder={f.placeholder ?? 'Type an item and press Enter'}
        style={{ width: '100%' }}
      />
    );
  }
  if (f.textarea) {
    return <Input.TextArea rows={f.rows ?? 3} placeholder={f.placeholder} />;
  }
  return <Input placeholder={f.placeholder} />;
}

function resolveTabForError(
  name: NamePath | undefined,
  fields: LocalizedFieldConfig[],
): string | null {
  if (!name || !Array.isArray(name) || name.length === 0) return null;
  const head = name[0];
  if (typeof head === 'string' && fields.some((f) => f.key === head)) return 'en';
  if (head === 'translations' && name.length >= 2) return String(name[1]);
  return null;
}

function tabLabel(label: string, hasError: boolean): React.ReactNode {
  if (!hasError) return label;
  return (
    <span>
      {label}
      <span style={{ color: '#ff4d4f', marginLeft: 4 }}>•</span>
    </span>
  );
}

/**
 * Language tabs: default locale writes top-level fields; other locales write
 * into translations[locale]. Place shared (non-localized) fields below this.
 */
export const LocalizedContentTabs: React.FC<{
  fields: LocalizedFieldConfig[];
  /** When false, omit the Shared divider (useful inside Card sections). Default true. */
  showSharedDivider?: boolean;
}> = ({ fields, showSharedDivider = true }) => {
  const { locales } = useActiveLocales();
  const form = Form.useFormInstance();
  const [activeKey, setActiveKey] = useState('en');
  const lastErrorSig = useRef('');

  const otherLocales = useMemo(
    () => locales.filter((l) => !isDefaultLocale(l.code)),
    [locales],
  );
  const defaultLabel =
    locales.find((l) => isDefaultLocale(l.code))?.label || 'English';

  return (
    <>
      <Form.Item noStyle shouldUpdate>
        {() => {
          const errorFields = form
            .getFieldsError()
            .filter((f) => f.errors.length > 0);
          const errorSig = errorFields.map((e) => e.name.join('.')).join('|');

          if (errorFields.length > 0 && errorSig !== lastErrorSig.current) {
            lastErrorSig.current = errorSig;
            const target = resolveTabForError(errorFields[0]?.name, fields);
            if (target && target !== activeKey) {
              queueMicrotask(() => setActiveKey(target));
            }
          } else if (errorFields.length === 0) {
            lastErrorSig.current = '';
          }

          const enHasError = fields.some((f) =>
            errorFields.some(
              (e) => Array.isArray(e.name) && e.name[0] === f.key,
            ),
          );

          const items = [
            {
              key: 'en',
              label: tabLabel(defaultLabel, enHasError),
              forceRender: true,
              children: (
                <div style={{ paddingTop: 8 }}>
                  {fields.map((f) => (
                    <Form.Item
                      key={f.key}
                      label={f.label}
                      name={f.key}
                      rules={f.required ? [{ required: true }] : undefined}
                    >
                      {renderInput(f)}
                    </Form.Item>
                  ))}
                </div>
              ),
            },
            ...otherLocales.map((locale) => {
              const localeHasError = errorFields.some(
                (e) =>
                  Array.isArray(e.name) &&
                  e.name[0] === 'translations' &&
                  e.name[1] === locale.code,
              );
              return {
                key: locale.code,
                label: tabLabel(locale.label, localeHasError),
                forceRender: true,
                children: (
                  <div style={{ paddingTop: 8 }}>
                    {fields.map((f) => (
                      <Form.Item
                        key={fieldTranslationKey(f)}
                        label={f.label}
                        name={['translations', locale.code, fieldTranslationKey(f)]}
                      >
                        {renderInput(f)}
                      </Form.Item>
                    ))}
                  </div>
                ),
              };
            }),
          ];

          return (
            <Tabs
              activeKey={activeKey}
              onChange={setActiveKey}
              items={items}
              style={{ marginBottom: 8 }}
            />
          );
        }}
      </Form.Item>
      {showSharedDivider && (
        <Divider orientation="left" plain>
          Shared
        </Divider>
      )}
    </>
  );
};

type ShowFieldValue = string | string[] | undefined;

/**
 * Read-only language tabs for show pages.
 * Default locale reads top-level record fields; other locales read translations.
 */
export const LocalizedContentShow: React.FC<{
  record: Record<string, any>;
  fields: LocalizedFieldConfig[];
}> = ({ record, fields }) => {
  const { locales } = useActiveLocales();
  const [activeLang, setActiveLang] = useState('en');

  const translationLocales = useMemo(() => {
    const fromData = Object.keys(record.translations || {}).filter((c) => !isDefaultLocale(c));
    const fromApi = locales.map((l) => l.code).filter((c) => !isDefaultLocale(c));
    return Array.from(new Set([...fromApi, ...fromData]));
  }, [record.translations, locales]);

  const localeLabels = useMemo(() => {
    const map: Record<string, string> = {};
    for (const l of locales) map[l.code] = l.label;
    const defaultLocale = locales.find((l) => isDefaultLocale(l.code));
    map.en = defaultLocale?.label || 'English';
    return map;
  }, [locales]);

  const renderValues = (values: Record<string, ShowFieldValue>) => (
    <div style={{ paddingTop: 8 }}>
      {fields.map((f) => {
        const tKey = fieldTranslationKey(f);
        const value = values[f.key] ?? values[tKey];
        let display: React.ReactNode = '—';
        if (Array.isArray(value) && value.length > 0) {
          display = (
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {value.map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </ul>
          );
        } else if (typeof value === 'string' && value.trim()) {
          display = f.textarea ? (
            <div style={{ whiteSpace: 'pre-wrap' }}>{value}</div>
          ) : (
            value
          );
        }
        return (
          <Field key={f.key} label={f.label}>
            {display}
          </Field>
        );
      })}
    </div>
  );

  const defaultValues: Record<string, ShowFieldValue> = {};
  for (const f of fields) {
    defaultValues[f.key] = record[f.key];
  }

  const tabItems = [
    {
      key: 'en',
      label: localeLabels.en,
      children: renderValues(defaultValues),
    },
    ...translationLocales.map((code) => {
      const data = (record.translations?.[code] || {}) as Record<string, ShowFieldValue>;
      return {
        key: code,
        label: localeLabels[code] || code,
        children: renderValues(data),
      };
    }),
  ];

  return (
    <Tabs
      activeKey={activeLang}
      onChange={setActiveLang}
      items={tabItems}
      style={{ marginBottom: 24 }}
    />
  );
};
